import React, { useContext } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Divider,
  Paper,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import CloseIcon from '@mui/icons-material/Close';
import { NotificationContext } from '../../context/NotificationContext';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { ru } from 'date-fns/locale';
import Button from './Button';
import { useTranslation } from 'react-i18next';

const NotificationPaper = styled(Paper)(({ theme }) => ({
  width: 350,
  maxHeight: 500,
  overflow: 'auto',
  padding: theme.spacing(2),
}));

const NotificationHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(1),
}));

const EmptyNotifications = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4),
  color: theme.palette.text.secondary,
}));

const UnreadIndicator = styled('div')(({ theme }) => ({
  width: 12,
  height: 12,
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.main,
  marginRight: theme.spacing(1),
}));

const NotificationPanel = ({ onClose }) => {
  const {
    notifications,
    loading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAllNotifications,
  } = useContext(NotificationContext);
  const navigate = useNavigate();
  const { t } = useTranslation('notifications');

  const getIconByType = (type) => {
    switch (type) {
      case 'success':
        return <CheckCircleIcon size="small" color="success" />;
      case 'warning':
        return <WarningIcon size="small" color="warning" />;
      case 'error':
        return <ErrorIcon size="small" color="error" />;
      case 'info':
      default:
        return <InfoIcon size="small" color="info" />;
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification._id);
    }

    if (notification.link) {
      onClose();
      navigate(notification.link);
    }
  };

  const formatDate = (date) => {
    try {
      return formatDistanceToNow(new Date(date), {
        addSuffix: true,
        locale: ru,
      });
    } catch (error) {
      return 'дата неизвестна';
    }
  };

  if (loading) {
    return (
      <NotificationPaper>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      </NotificationPaper>
    );
  }

  return (
    <NotificationPaper elevation={0}>
      <NotificationHeader>
        <Typography variant="h6" sx={{ fontSize: '0.95rem' }}>
          {t('panel.title')}
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </NotificationHeader>

      {notifications.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mb: 1,
            '& .MuiButton-root': {
              fontSize: '0.75rem',
              textTransform: 'none',
            },
          }}
        >
          {notifications.some((notification) => !notification.read) && (
            <Button variant="text" size="small" onClick={markAllAsRead}>
              {t('panel.markAllRead')}
            </Button>
          )}
          <Button
            variant="text"
            size="small"
            color="error"
            onClick={clearAllNotifications}
            sx={{ ml: 'auto' }}
          >
            {t('panel.deleteAll')}
          </Button>
        </Box>
      )}

      <Divider />

      {notifications.length === 0 ? (
        <EmptyNotifications>
          <InfoIcon fontSize="large" sx={{ mb: 1 }} />
          <Typography sx={{ fontSize: '0.875rem' }}>
            {t('panel.empty')}
          </Typography>
        </EmptyNotifications>
      ) : (
        <List sx={{ pt: 0 }}>
          {notifications.map((notification) => (
            <ListItem
              key={notification._id}
              button
              alignItems="flex-start"
              onClick={() => handleNotificationClick(notification)}
              sx={{
                bgcolor: notification.read ? 'transparent' : 'action.hover',
                borderRadius: '10px',
                mb: 1,
              }}
            >
              <ListItemIcon sx={{ minWidth: 38 }}>
                {getIconByType(notification.type)}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {!notification.read && <UnreadIndicator />}
                    <Typography
                      variant="subtitle2"
                      component="span"
                      sx={{ fontSize: '0.875rem' }}
                    >
                      {notification.title}
                    </Typography>
                  </Box>
                }
                secondary={
                  <React.Fragment>
                    <Typography
                      component="span"
                      variant="body2"
                      color="text.primary"
                      sx={{
                        display: 'block',
                        fontSize: '0.8125rem',
                      }}
                    >
                      {notification.message}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.75rem' }}
                    >
                      {formatDate(notification.createdAt)}
                    </Typography>
                  </React.Fragment>
                }
              />
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {!notification.read && (
                  <Tooltip title={t('panel.markAsRead')}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        markAsRead(notification._id);
                      }}
                    >
                      <CheckCircleIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title={t('panel.delete')}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteNotification(notification._id);
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </ListItem>
          ))}
        </List>
      )}
    </NotificationPaper>
  );
};

export default NotificationPanel;
